import * as vp from 'vopay';
import { useEffect, useState } from 'react';
import axios from 'axios';

import { useBlockingLoader } from 'shared';
import vopayApi from '../../services/api.config';
import useTransactionApprovalAccess from '@/hooks/useTransactionApprovalAccess';

type HookProps = {
  api: vp.Api;
};

type ReturnTuple = [
  vp.ApprovalInboxTransactionsResponse | undefined,
  (requestParams: vp.ApprovalInboxTransactionsRequest) => void,
  () => void,
  (requestParams: vp.ApprovalInboxTransactionsRequest) => Promise<vp.ApprovalInboxTransactionsResponse>,
];

function useFetchApprovalTransactions({ api }: HookProps): ReturnTuple {
  const source = axios.CancelToken.source();

  const [params, setParams] = useState<vp.ApprovalInboxTransactionsRequest>();
  const [response, setResponse] = useState<vp.ApprovalInboxTransactionsResponse>();
  const [shouldSendRequest, setSendRequest] = useState(false);
  const blockingLoader = useBlockingLoader();
  const canUseTransactionApproval = useTransactionApprovalAccess();

  useEffect(() => {
    if (!canUseTransactionApproval) {
      shouldSendRequest && setSendRequest(false);

      return;
    }

    // This path is only driven by ApprovalWorkflowProvider's background fetches, so it must not
    // raise the fullscreen blocking loader; interactive callers use the awaitable fetch below.
    const sendRequest = async (requestParams: vp.ApprovalInboxTransactionsRequest) => {
      const res = await api.fetchApprovalTransactions(requestParams, source);

      setResponse(res);
      setSendRequest(false);
    };

    if (params && shouldSendRequest) void sendRequest(params);

    return () => {
      source.cancel();
    };
  }, [shouldSendRequest]);

  return [
    response,
    (requestParams: vp.ApprovalInboxTransactionsRequest) => {
      setParams(requestParams);
      setSendRequest(true);
    },
    () => {
      setResponse(undefined);
      setSendRequest(false);
    },
    async (requestParams: vp.ApprovalInboxTransactionsRequest) => {
      blockingLoader.show();

      try {
        const res = await api.fetchApprovalTransactions(requestParams, source);
        setResponse(res);

        return res;
      } finally {
        blockingLoader.hide();
      }
    },
  ];
}

export default vopayApi.withApiHook<HookProps, ReturnTuple>(useFetchApprovalTransactions);
